import { useState } from 'react';
import { categories, regions, timeRanges, sortOptions } from '@/mocks/categories';

interface FilterBarProps {
  type: string;
  region: string;
  timeRange: string;
  sort: string;
  onTypeChange: (value: string) => void;
  onRegionChange: (value: string) => void;
  onTimeChange: (value: string) => void;
  onSortChange: (value: string) => void;
}

const chipBase =
  'px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap';

export default function FilterBar({
  type,
  region,
  timeRange,
  sort,
  onTypeChange,
  onRegionChange,
  onTimeChange,
  onSortChange,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between p-4 md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-background-300 text-sm font-medium text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line"></i>
          筛选条件
          <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
        </button>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value)}
          className="px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 cursor-pointer focus:outline-none"
        >
          {sortOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className={`${open ? 'block' : 'hidden'} md:block p-4 md:p-5 space-y-4 md:space-y-5`}>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 md:items-start">
          <span className="shrink-0 text-sm font-medium text-foreground-500 md:w-20 md:pt-1.5">
            赛事类型
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onTypeChange('all')}
              className={`${chipBase} ${
                type === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onTypeChange(c.id)}
                className={`${chipBase} ${
                  type === c.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-2 md:gap-4 md:items-center">
          <span className="shrink-0 text-sm font-medium text-foreground-500 md:w-20">举办地区</span>
          <select
            value={region}
            onChange={(e) => onRegionChange(e.target.value)}
            className="w-full md:w-48 px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 cursor-pointer focus:border-accent-400 focus:outline-none"
          >
            <option value="">全部地区</option>
            {regions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col md:flex-row gap-2 md:gap-4 md:items-start">
          <span className="shrink-0 text-sm font-medium text-foreground-500 md:w-20 md:pt-1.5">
            举办时间
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onTimeChange('')}
              className={`${chipBase} ${
                timeRange === ''
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-900'
              }`}
            >
              全部时间
            </button>
            {timeRanges.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => onTimeChange(t.value)}
                className={`${chipBase} ${
                  timeRange === t.value
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4 pt-1 border-t border-background-200">
          <span className="shrink-0 text-sm font-medium text-foreground-500 md:w-20 pt-3">排序方式</span>
          <div className="flex flex-wrap gap-2 pt-3">
            {sortOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => onSortChange(o.value)}
                className={`${chipBase} ${
                  sort === o.value
                    ? 'bg-secondary-700 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-secondary-200'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}